import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { AxiosError, AxiosResponse } from "axios";
import { createContext, use, useState } from "react";

import {
    useBFFProductService,
    useBFFProductServiceApi
} from "@/api/hooks/productService.hook";
import { MessageComponent } from "@/components/General/Messages/messages.component";
import { RouterModal } from "@/components/General/Modal/routerModal";
import { FORM_ID } from "@/components/Product/Reviews/writeReview/writeReview.component";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { ReviewSubmissionModel } from "@/types/service/reviews/reviewSubmission.model";
import { useModalRouter } from "@/utils/hooks/useModalRouter";

import { WriteReviewContainer } from "../writeReview/writeReview.container";
import { useDeviceFingerPrinting } from "./deviceFingerPrinting.context";

type WriteReviewContextValue = {
    reviewAuthor: string | null;
    reviewSubmission: ReviewSubmissionModel | null;
    open: (initialRating?: number) => void;
};

type WriteReviewContextProviderProps = {
    sku: string;
};

export const WriteReviewContext = createContext<
    WriteReviewContextValue | undefined
>(undefined);
WriteReviewContext.displayName = "WriteReviewContext";

export const WriteReviewContextProvider = ({
    sku,
    children
}: RequireChildrenFor<WriteReviewContextProviderProps>) => {
    const { t } = useGranite();
    const { getDeviceFingerPrint } = useDeviceFingerPrinting();

    const [errors, setErrors] = useState<
        Array<{ code: string; message?: string }>
    >([]);
    const [fieldErrors, setFieldErrors] = useState<
        Partial<Record<keyof ReviewSubmissionModel, string>>
    >({});

    const [initialRating, setInitialRating] = useState<number | null>(null);

    const { open: openModal } = useModalRouter(ModalType.ReviewWrite);

    const [reviewSubmission, setReviewSubmission] =
        useState<ReviewSubmissionModel | null>(null);
    const [reviewAuthor, setReviewAuthor] = useState<string | null>(null);

    const [_, initiateSubmit] = useBFFProductServiceApi<
        undefined,
        undefined,
        {
            author?: string;
            review?: ReviewSubmissionModel;
        }
    >(
        {
            url: "/reviews/initiateSubmit",
            method: "POST"
        },
        {
            manual: true,
            useCache: false
        }
    );

    const productService = useBFFProductService();

    const handleOnOpen = async (initialRating?: number) => {
        if (!isDefined(reviewSubmission)) {
            const { data } = await initiateSubmit({
                params: {
                    sku: sku
                }
            });
            const { author, review } = data;

            if (isDefined(review)) {
                setReviewSubmission(review);
            }

            if (isDefined(author)) {
                setReviewAuthor(author);
            }
        }

        setInitialRating(initialRating ?? null);
        openModal();
    };

    const handleOnSubmit = async (values: ReviewSubmissionModel) => {
        const deviceFingerprint = await getDeviceFingerPrint();
        try {
            await productService.post<
                {},
                AxiosResponse<{}, any>,
                ReviewSubmissionModel
            >("/reviews/progressiveSubmit", values, {
                params: {
                    sku: sku,
                    deviceFingerprint: deviceFingerprint
                }
            });

            setReviewSubmission(values);

            if (errors) {
                setErrors([]);
            }

            if (fieldErrors) {
                setFieldErrors({});
            }
        } catch (error: any) {
            if (error instanceof AxiosError) {
                const axiosError = error as AxiosError<{
                    fieldErrors?: Partial<
                        Record<keyof ReviewSubmissionModel, string>
                    >;
                    errors?: Array<{ code: string; message?: string }>;
                }>;

                if (axiosError.response?.data.errors) {
                    setErrors(axiosError.response?.data.errors);
                }

                if (axiosError.response?.data.fieldErrors) {
                    setFieldErrors(axiosError.response?.data.fieldErrors);
                }
            }
        }
    };

    const handleOnToggle = () => {
        setInitialRating(null);
    };

    return (
        <WriteReviewContext
            value={{
                reviewAuthor: reviewAuthor,
                reviewSubmission: reviewSubmission,
                open: handleOnOpen
            }}
        >
            {children}
            <RouterModal
                modalType={ModalType.ReviewWrite}
                title={t("general.reviews.write")}
                formId={FORM_ID}
                size={"lg"}
                onOpenEnd={handleOnToggle}
                onCloseEnd={handleOnToggle}
                withFooter={false}
            >
                {errors.map((error) => (
                    <MessageComponent
                        key={error.code}
                        message={{
                            dismissible: false,
                            status: "danger",
                            text: t(
                                `product.reviews.writeReview.error.${error.code}`
                            ),
                            id: error.code
                        }}
                    />
                ))}
                <WriteReviewContainer
                    initialRating={initialRating}
                    reviewSubmission={reviewSubmission}
                    reviewAuthor={reviewAuthor}
                    fieldErrors={fieldErrors}
                    onSubmit={handleOnSubmit}
                />
            </RouterModal>
        </WriteReviewContext>
    );
};

export const useWriteReviewContext = () => {
    const context = use<WriteReviewContextValue | undefined>(
        WriteReviewContext
    );

    if (!isDefined(context)) {
        throw new Error(`${WriteReviewContext.displayName} is undefined.`);
    }

    return context;
};
